import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import adminApi from '../../Api/adminApi';
import useAdminStyles from './adminStyles';
import useAdminQuery from './useAdminQuery';
import StateCard from './StateCard';
import OverviewTab from './OverviewTab';
import UsersTab from './UsersTab';
import SubscriptionsTab from './SubscriptionsTab';
import LessonsTab from './LessonsTab';
import SamsungTab from './SamsungTab';
import UserDetailDrawer from './UserDetailDrawer';

const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'lessons', label: 'Lessons' },
    { id: 'samsung', label: 'Samsung' },
];

/**
 * Internal admin page: subscriptions, user activity and billing in one place.
 * It is reachable only by URL (nothing in the app links to it). The active tab
 * and the opened user live in the query string (?tab=&user=) so any view can be
 * linked. Access is enforced by the backend on every admin route; the page only
 * asks /admin/me once to know whether to render at all.
 */
const AdminPage = () => {
    const classes = useAdminStyles();
    const history = useHistory();
    const location = useLocation();
    // Bumped after a user action so open tables re-fetch.
    const [refreshKey, setRefreshKey] = useState(0);
    const me = useAdminQuery(() => adminApi.getAdminInfo(), []);
    const isAdmin = !!me.data?.email;

    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    const tab = TABS.some((t) => t.id === requestedTab) ? requestedTab : 'overview';
    const userId = params.get('user');

    const setParams = useCallback(
        (changes) => {
            const next = new URLSearchParams(history.location.search);
            Object.keys(changes).forEach((key) => {
                if (changes[key] === null || changes[key] === undefined || changes[key] === '') {
                    next.delete(key);
                } else {
                    next.set(key, changes[key]);
                }
            });
            const search = next.toString();
            history.replace({ pathname: history.location.pathname, search: search ? `?${search}` : '' });
        },
        [history]
    );

    const selectTab = (id) => setParams({ tab: id === 'overview' ? null : id, user: null });
    const openUser = useCallback((id) => setParams({ user: id }), [setParams]);
    const closeUser = useCallback(() => setParams({ user: null }), [setParams]);
    const onUserUpdated = useCallback(() => setRefreshKey((k) => k + 1), []);

    const renderTab = () => {
        const common = { onOpenUser: openUser, refreshKey };
        switch (tab) {
            case 'users':
                return <UsersTab {...common} selectedUserId={userId} />;
            case 'subscriptions':
                return <SubscriptionsTab {...common} selectedUserId={userId} />;
            case 'lessons':
                return <LessonsTab />;
            case 'samsung':
                return <SamsungTab />;
            default:
                return <OverviewTab />;
        }
    };

    return (
        <Grid item xs={12}>
            <LandingSection id="admin" background="none" gutter={0} maxWidth={10000} paddingTop={8} paddingBottom={56}>
                <div className={classes.intro}>
                    <div>
                        <div className={classes.eyebrowRow}>
                            <SectionEyebrow>Internal</SectionEyebrow>
                        </div>
                        <h1 className={classes.title}>Admin</h1>
                        <p className={classes.lead}>Subscriptions, activity and billing at a glance.</p>
                    </div>
                    {isAdmin ? <span className={classes.roleBadge}>{me.data.email}</span> : null}
                </div>

                {!isAdmin ? (
                    <StateCard
                        isLoading={me.isLoading}
                        isForbidden={me.isForbidden || (!me.isLoading && !me.isError)}
                        isError={me.isError}
                    />
                ) : (
                    <>
                        <ul className={classes.tabs} role="tablist">
                            {TABS.map((t) => (
                                <li key={t.id} role="presentation">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={tab === t.id}
                                        className={tab === t.id ? classes.tabActive : classes.tab}
                                        onClick={() => selectTab(t.id)}
                                    >
                                        {t.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {renderTab()}
                        <UserDetailDrawer
                            userId={userId}
                            open={!!userId}
                            onClose={closeUser}
                            onUpdated={onUserUpdated}
                        />
                    </>
                )}
            </LandingSection>
        </Grid>
    );
};

export default AdminPage;
